import React, { useState } from 'react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Settings, User, LogOut, Menu, X, Key, BookOpen, Download, LifeBuoy } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const UserSidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuStructure = [
    { path: '/panel', labelKey: 'sidebar.user.home', icon: LayoutDashboard, end: true },
    { path: '/panel/downloads', labelKey: 'sidebar.user.downloads', icon: Download },
    { path: '/panel/my-licenses', labelKey: 'sidebar.user.myLicenses', icon: Key },
    { path: '/panel/posts', labelKey: 'sidebar.user.installationInstructions', icon: BookOpen },
    { path: '/panel/plugin-settings', labelKey: 'sidebar.user.pluginSettings', icon: Settings },
    { path: '/panel/support', labelKey: 'sidebar.user.support', icon: LifeBuoy },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-sidebar-bg z-50 flex items-center justify-between px-4 shadow-lg">
        <NavLink
          to="/panel"
          end
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <Key className="w-8 h-8 text-primary-400" />
          <span className="text-white font-bold text-lg">License MS</span>
        </NavLink>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="text-white p-2 hover:bg-sidebar-hover rounded-lg transition-colors"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-sidebar-bg flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-16 flex items-center gap-3 px-6 border-b border-gray-700/50">
          <Key className="w-8 h-8 text-primary-400 flex-shrink-0" />
          <span className="text-white font-bold text-lg truncate">License MS</span>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {menuStructure.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                className={`sidebar-item w-full ${active ? 'active' : ''}`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-sidebar-text'}`} />
                <span className="text-sm font-medium flex-1 text-left">{t(item.labelKey)}</span>
              </button>
            );
          })}

          <div className="border-t border-gray-700/30 my-2" />

          <NavLink
            to="/panel/profile"
            onClick={() => setMobileOpen(false)}
            className={`sidebar-item ${location.pathname === '/panel/profile' ? 'active' : ''}`}
          >
            <User className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{t('sidebar.user.profile')}</span>
          </NavLink>

          <button
            onClick={handleLogout}
            className="sidebar-item w-full text-left text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">{t('sidebar.user.exit')}</span>
          </button>
        </nav>
      </aside>
    </>
  );
};

export default UserSidebar;